import React, { useState, useRef, FormEvent } from 'react';
import styled from 'styled-components';
import { Button, RowSpacer } from '../atoms';
import { Col, Row } from 'react-grid-system';
import { ResponseType, useValidateOtpMutation, ValidateOtpMutation } from '@/graphql/generated';
import { useLocation, useNavigate } from 'react-router';
import { pathURL } from '@/components/constants/urls';
import { FetchResult } from '@apollo/client';
import { useErrorHandlerContext, useOpeningElementsControllerContext, useUserLanguage } from '@/customHooks';
import { WarnText } from '../atoms/WarnText';

const textsData = {
  en: {
    button: 'Submit',
    confirmationText: {
      title: 'Success',
      description: 'The operation was made successfully',
      button: 'Continue'
    },
  },
  es: {
    button: 'Enviar',
    confirmationText: {
      title: 'Exitoso',
      description: 'La operación se realizó de manera exitosa',
      button: 'Continuar'
    },
  }
}

const FormWrapper = styled.form`
  max-width: 360px;
`

const Container = styled(Col)`
  display: flex;
  gap: 0.5rem;
  margin-bottom: 1rem;
`;

const Input = styled.input`
  width: 3rem;
  height: 3rem;
  text-align: center;
  border: 1px solid #d1d5db; /* gray-300 */
  border-radius: 0.375rem; /* rounded */
  font-size: 1.125rem; /* text-lg */
  font-weight: bold;
  outline: none;

  &:focus {
    border-color: ${({ theme }) => theme.colors.primary1}; /* indigo-500 */
  }
`;

export const OTPInputForm = () => {
  const [otp, setOtp] = useState<string[]>(new Array(6).fill('')); // Estado para almacenar los valores de los campos
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]); // Referencias para acceder a los inputs

  const [isWarnHidden, setIsWarnHidden] = useState(true)
  const userLanguage = useUserLanguage();
  const texts = userLanguage === 'es' ? textsData.es : textsData.en;

  const { confirmDialogModalOpen } = useOpeningElementsControllerContext()

  const { open: errorHadle } = useErrorHandlerContext()

  const [validateOtp, { loading }] = useValidateOtpMutation()

  const navigate = useNavigate()

  const location = useLocation()

  // Manejar cambios en un campo
  const handleChange = (index: number, value: string): void => {
    setIsWarnHidden(true)
    const newOtp = [...otp];
    newOtp[index] = value;
    setOtp(newOtp);

    // Mover el foco al siguiente campo si se ingresa un valor
    if (value && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  // Manejar el pegado de un código completo
  const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>): void => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text').slice(0, 6).split(''); // Obtener los primeros 6 caracteres del portapapeles
    const newOtp = [...otp];

    pastedData.forEach((value, i) => {
      if (i < 6) {
        newOtp[i] = value;
      }
    });
    setOtp(newOtp);
    inputRefs.current[pastedData.length - 1]?.focus(); // Mover el foco al último campo pegado
  };

  // Manejar el borrado con "Backspace"
  const handleKeyDown = (index: number, e: React.KeyboardEvent<HTMLInputElement>): void => {
    if (e.key === 'Backspace' && !otp[index] && index > 0) {
      // Retroceder al campo anterior si se presiona "Backspace" y el campo actual está vacío
      inputRefs.current[index - 1]?.focus();
    }
  };

  const onSuccessHandle = (response: FetchResult<ValidateOtpMutation>, otpCode: number) => {
    const validateOTPresponse = response.data?.validateOtp
    
    if (!validateOTPresponse) return

    console.log('validate: ', response.data?.validateOtp)
    console.log('registration: ', location.state?.isForRegister)

    if(validateOTPresponse.responseType === ResponseType.Success) {
      if (location.state?.isForRegister) {
        confirmDialogModalOpen({
          title: texts.confirmationText.title,
          description: texts.confirmationText.description,
          buttonText: texts.confirmationText.button
        })
        navigate(`/${pathURL.LOGIN}`)
      } else {
        navigate(
          `/${pathURL.LOGIN}/${pathURL.RESET_PASSWORD}`,
          { state: { otpCode }}
        )
      }
    } else if (validateOTPresponse.responseType === ResponseType.Failed) {
      setIsWarnHidden(false)
    }
  }

  const onSubmitHandle = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    const otpCode = parseInt(otp.join(''))
    
    validateOtp({ variables: {
      code: otpCode,
      isForRegistration: location.state?.isForRegister
    }})
      .then((response) => onSuccessHandle(response, otpCode))
      .catch(errorHadle)
  }

  return (
    <FormWrapper onSubmit={onSubmitHandle}>
      <Row>
        <Container>
            {otp.map((value, index) => (
              <Input
                key={index}
                type="text"
                maxLength={1}
                value={value}
                onChange={(e) => handleChange(index, e.target.value)}
                onKeyDown={(e) => handleKeyDown(index, e)}
                onPaste={handlePaste}
                ref={(el) => {inputRefs.current[index] = el}}
                pattern="[0-9]"
              />
            ))}
        </Container>
      </Row>
      <Row>
        <Col>
          <WarnText
            text='Codigo OTP no pudo confirmarse, por favor verifique el código e intente nuevamente'
            hidden={isWarnHidden}
          />
        </Col>
      </Row>
      <RowSpacer height={3} />
      <Row>
        <Col xs={12}>
          <Button submit isLoading={loading}>{texts.button}</Button>
        </Col>
      </Row>
    </FormWrapper>
  );
};
